import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../../api/axois";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import { getImageUrl } from "../../utils/imageUtils";
import {
  FaBed,
  FaShower,
  FaSnowflake,
  FaTv,
  FaWifi,
  FaConciergeBell,
  FaLaptop,
  FaStar,
  FaEdit,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import styles from "./RoomDetails.module.css";


const RoomDetails = () => {
  const { id } = useParams();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [edited, setEdited] = useState(false);
  const [price, setPrice] = useState(0);
  const [roomCount, setRoomCount] = useState(0);
  const [status, setStatus] = useState("");
  const [visibleReviews, setVisibleReviews] = useState({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  // Demo reviews, since API does not provide reviews
  const reviews = [
    {
      id: 1,
      name: "Eleanor Pena",
      text: "Room was clean and exactly as described. Would stay again!",
      rating: 5,
    },
    {
      id: 2,
      name: "Guy Hawkins",
      text: "Nice space, but Wi-Fi was a bit slow at times.",
      rating: 3,
    },
    {
      id: 3,
      name: "Brooklyn Simmons",
      text: "Fantastic service and comfortable bed. 10/10.",
      rating: 5,
    },
    {
      id: 4,
      name: "Albert Flores",
      text: "Good value for money. Will recommend to friends.",
      rating: 4,
    },
  ];
  // Helper to determine boolean availability
  const isAvailable = (status || "").toLowerCase() === "available";

  useEffect(() => {
    setVisibleReviews(reviews.reduce((acc, r) => ({ ...acc, [r.id]: true }), {}));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/rooms/${id}/details`)
      .then((res) => {
        setRoom(res.data.room);
        setPrice(res.data.room.price_per_night || 0);
        setRoomCount(res.data.room.capacity || 0);
        setStatus(res.data.room.status || "");
        setLoading(false);
      })
      .catch((err) => {
        setError("Room not found");
        setLoading(false);
      });
  }, [id]);

  // Toggle room availability via API
  const handleToggleAvailability = async () => {
    setAvailabilityLoading(true);
    try {
      const res = await api.put(`/rooms/${id}/availability`, { is_available: !isAvailable });
      const newStatus = res.data.room.is_available ? "Available" : "Unavailable";
      setStatus(newStatus);
    } catch (err) {
      alert("Failed to update availability");
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const toggleVisibility = (id) => {
    setVisibleReviews((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (loading) {
    return <div className={styles.container}><Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} /><div className={styles.content}><Header onMenuClick={() => setSidebarOpen(true)} /><p>Loading...</p></div>{sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />}</div>;
  }
  if (error || !room) {
    return <div className={styles.container}><Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} /><div className={styles.content}><Header onMenuClick={() => setSidebarOpen(true)} /><p>{error || "Room not found"}</p></div>{sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />}</div>;
  }

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <h1 className={styles.pageTitle}>Room Details</h1>

        {/* Room Images Gallery */}
        {room.images && room.images.length > 0 && (
          <div className={styles.imageGrid}>
            {room.images.map((img, idx) => (
              <div className={styles.imageWrapper} key={idx}>
                <img
                  src={getImageUrl(img)}
                  alt={`Room ${idx + 1}`}
                  className={styles.roomImage}
                />
              </div>
            ))}
          </div>
        )}

        {/* Room Info Card */}
        <div className={styles.infoCard}>
          <div className={styles.infoHeader}>
            <h2 className={styles.roomName}>{room.roomType || room.title || "Room"}</h2>
            <span
              className={
                `${styles.statusBadge} ` +
                ((status || "").toLowerCase().includes("unavailable")
                  ? styles.unavailable
                  : (status || "").toLowerCase().includes("available")
                  ? styles.available
                  : styles.occupied)
              }
            >
              {status}
            </span>
          </div>
          <div className={styles.infoDetails}>
            <div>
              <span className={styles.infoLabel}>Room ID:</span> #{room.roomID || room.id}
            </div>
            <div>
              <span className={styles.infoLabel}>Room Number:</span> {room.room_number || "-"}
            </div>
            <div>
              <span className={styles.infoLabel}>Capacity:</span> {roomCount}
            </div>
            <div>
              <span className={styles.infoLabel}>Price:</span> ₦{price ? price.toLocaleString() : 0} Per Night
            </div>
            <div style={{ marginTop: '1rem' }}>
            </div>
          </div>
        </div>

        {/* Modal for Mark as Unavailable */}
        {showModal && (
          <div className={styles.modalBackdrop}>
            <div className={styles.modal}>
              <p>Are you sure you want to mark this room as unavailable?</p>
              <div className={styles.modalButtons}>
                <button
                  className={styles.confirmBtn}
                  onClick={() => {
                    setStatus("Unavailable");
                    setShowModal(false);
                  }}
                >
                  Confirm
                </button>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal for Save Edits */}
        {showEditModal && (
          <div className={styles.modalBackdrop}>
            <div className={styles.modal}>
              <p>Are you sure you want to save these changes?</p>
              <div className={styles.modalButtons}>
                <button
                  className={styles.confirmBtn}
                  onClick={() => {
                    setShowEditModal(false);
                    setEdited(false);
                  }}
                >
                  Confirm
                </button>
                <button
                  className={styles.cancelBtn}
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

{/* 
        <div className={styles.roomRating}>
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} color={i < 4 ? "gold" : "#ccc"} />
          ))}
          <span className={styles.ratingCount}>(2.3k reviews)</span>
        </div> */}

        <div className={styles.editForm}>
         

          {/* <div className={styles.formGroup}>
            <label>Edit Room Number</label>
            <div className={styles.inputIconModern}>
              <input
                type="number"
                value={roomCount}
                onChange={(e) => {
                  setRoomCount(Number(e.target.value));
                  setEdited(true);
                }}
              />
              <span className={styles.inputIconRight}><FaEdit /></span>
            </div>
          </div> */}
        </div>

        {edited && (
          <button className={styles.saveBtn} onClick={() => setShowEditModal(true)}>
            Save Changes
          </button>
        )}


        <div className={styles.roomDescriptionCard}>
          <h3>Description</h3>
          <p className={styles.roomDescription}>{room.description || "No description provided."}</p>
        </div>

        <div className={styles.featuresGrid}>
          <div className={styles.feature}>
            <FaBed /> King-size bed
          </div>
          <div className={styles.feature}>
            <FaShower /> Private bathroom
          </div>
          <div className={styles.feature}>
            <FaSnowflake /> Air conditioning
          </div>
          <div className={styles.feature}>
            <FaWifi /> Wi-Fi
          </div>
          <div className={styles.feature}>
            <FaTv /> Smart TV
          </div>
          <div className={styles.feature}>
            <FaLaptop /> Workspace
          </div>
          <div className={styles.feature}>
            <FaConciergeBell /> 24/7 Room Service
          </div>
        </div>

        
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default RoomDetails;
